<script lang="ts">
import { defineComponent, PropType, getCurrentInstance, ref, watch, nextTick, onMounted } from 'vue'

/**
 * @fileoverview
 *  機能と DOM のみの準ヘッドレスコンポーネントです。
 *  実際に利用する際は、ラッパーコンポーネントを用意し、deep オブション付きのスタイルを定義した上での運用を想定しており、
 *  よってこのコンポーネントのみでの使用はできません。
 * @example /app/frontend/components/form/SelectorDefault.vue
 */
export default defineComponent({
  name: 'SelectorCore',

  props: {
    /**
     * <select> 要素の valueを受け取る
     * 値が null の場合は value 指定なしとして内部データで処理を行う
     */
    modelValue: {
      type: [String, Number, Boolean],
      default: null,
    },

    /**
     * <select> 要素の <option> 部分に渡す配列
     *
     * @example [1, 2, 3, 4] : number 型の配列
     * @example ['1', '2', '3', '4'] : String 型の配列
     * @example [{ value: '1', label: 'ラベル1' }] : Object 型の配列 value と画面に表示する内容が異なる場合
     *
     */
    items: {
      type: Array as PropType<SelectorOption[]>,
      required: true,
    },

    /**
     * フォームの name 属性
     * 任意プロパティ
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * <option> の末尾に付与される単位
     * 任意プロパティ
     *
     * @example unit="個" => <option>1個</option>
     */
    unit: {
      type: String,
      default: '',
    },

    /**
     * セレクターの非活性フラグ
     * 任意プロパティ
     *
     * @usage 活性状態を管理する親の data を受け渡す
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },

    /**
     * プレースホルダー用ラベル
     * 任意プロパティ
     */
    defaultSelectorLabel: {
      type: String,
      default: '',
    },

    /**
     * プレースホルダー用の値
     * 任意プロパティ
     */
    defaultSelectorValue: {
      type: [String, Number],
      default: null,
    },

    /**
     * inline-block 表示するかどうか
     */
    inline: {
      type: Boolean,
      default: false,
    },

    /**
     * エラー発生状態かどうか
     * ※ エラー状態は外から受け取る
     */
    isError: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'change'],

  setup(props, context) {
    /**
     * コンポーネント内部でもつ選択状態の値
     * 親コンポーネントから value プロパティ を受け取らない場合は、このプロパティの値が selector に反映される
     */
    const localValue = ref()

    watch(() => props.modelValue, () => {
      localValue.value = props.modelValue
    })

    /**
     * [リアクティブデータの描画漏れ回避策]
     *
     * option の値が更新されたら強制描画する
     */
    watch(() => props.items, (newValue) => {
      if (newValue.length) {
        const instance = getCurrentInstance()
        nextTick(() => {
          instance?.proxy?.$forceUpdate()
        })
      }
    }, { deep: true })

    onMounted(() => {
      localValue.value = (props.modelValue !== null)
        ? props.modelValue
        : (props.defaultSelectorValue !== null)
          ? props.defaultSelectorValue
          : localValue.value
    })

    const init = () => {
      // 親から受け取った value がオプション選択肢にない場合は、先頭の値を選択する
      const itemValues = props.items.map(item => itemValue(item))
      itemValues.push(props.defaultSelectorValue)

      if (!itemValues.includes(props.modelValue) && props.items.length) {
        // context.emit('update:modelValue', props.items[0].value)
        context.emit('update:modelValue', undefined)
      }
    }

    /**
     * セレクターの選択項目変更時の emit 処理
     *
     * @usage ページの呼び出し側で v-model で親の data と紐付ける (v-model の内部的に v-on と糖衣構文となるので受取可能となる)
     */
    const updateValue = (event: Event) => {
      // 親から value プロパティを受け取っている場合
      if (props.modelValue !== null) {
        const currentValue = props.modelValue
          ? props.modelValue.toString() // event.target.value が文字列のため比較対象と型を合わせる
          : props.defaultSelectorValue

        const targetOption = props.items.find(item => itemValue(item).toString() === (event.target as HTMLInputElement).value)
        // 変更後の値が item リストに存在しない場合はデフォルト選択値と見なす
        const changedValue = targetOption
          ? itemValue(targetOption)
          : props.defaultSelectorValue

        context.emit('update:modelValue', changedValue)

        // 値に変更があった場合のみ change イベントを emit する
        if (currentValue !== changedValue) {
          context.emit('change', changedValue)
        }
      } else {
        // コンポーネント内部の値を保存
        localValue.value = (event.target as HTMLInputElement).value
        context.emit('update:modelValue', localValue.value)
      }
    }

    /**
     * option の値
     *
     * @param item {String|Object}
     * @return {String}
     */
    const itemValue = (item: SelectorOption) => {
      return typeof item === 'object' ? item.value : item
    }

    /**
     * option 表示用のラベル
     *
     * @param item {String|Object}
     * @return {String}
     */
    const itemLabel = (item: SelectorOption) => {
      return typeof item === 'object' ? item.label : item
    }

    init()

    return {
      // data
      localValue,

      // methods
      updateValue,
      itemValue,
      itemLabel,
    }
  }
})
</script>

<template>
  <div
    :class="[
      'o-selector',
      { 'is-inline': inline },
    ]"
    :data-disabled="isDisabled"
    :data-error="isError">
    <label
      :class="[
        'o-selector--label',
        { 'is-inline': inline },
      ]"
      :data-disabled="isDisabled"
      :data-error="isError">
      <select
        class="o-selector--items"
        :value="localValue"
        :name="name"
        :disabled="isDisabled"
        :data-error="isError"
        @change="updateValue">

        <!-- 初期値 -->
        <option
          v-if="defaultSelectorLabel.length > 0"
          class="o-selector--item"
          :value="defaultSelectorValue">
          {{ defaultSelectorLabel }}
        </option>

        <option
          v-for="item in items"
          :key="itemLabel(item)"
          class="o-selector--item"
          :value="itemValue(item)">
          {{ itemLabel(item) }}{{ unit }}
        </option>
      </select>
    </label>
  <!-- /.o-selector --></div>
</template>
